import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { LOGO_URL } from "@/lib/constants";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin } from "lucide-react";

const ROLES = [
  { id: "client", label: "Client", desc: "I need services" },
  { id: "professional", label: "Professional", desc: "I provide services" },
  { id: "caregiver", label: "Caregiver", desc: "I manage a patient's bookings" },
];

export default function Auth() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const presetRole = params.get("role");
  const validPreset = ["client", "professional", "caregiver"].includes(presetRole);
  const [busy, setBusy] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [forgot, setForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  const doForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", {
        email: forgotEmail, origin_url: window.location.origin,
      });
      setForgotSent(true);
      toast.success(data.message || "If an account exists, a reset link has been sent");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not send reset link");
    } finally {
      setBusy(false);
    }
  };
  const refCode = (params.get("ref") || "").toUpperCase();
  const [reg, setReg] = useState({
    name: "", email: "", password: "", role: validPreset ? presetRole : "client",
    phone: "", city: "", state: "", zip: "", lat: null, lng: null, home_pledge: false,
    ...(refCode ? { referral_code: refCode } : {}),
  });

  const geolocate = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setReg((r) => ({ ...r, lat: pos.coords.latitude, lng: pos.coords.longitude }));
        toast.success("Location captured");
      },
      () => toast.error("Could not get location")
    );
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    if (reg.role === "client" && (!reg.phone || reg.phone.trim().length < 7)) {
      toast.error("Please provide a valid phone number");
      return;
    }
    if (reg.role === "client" && !reg.home_pledge) {
      toast.error("Please agree to the pledge to be home when you book a service");
      return;
    }
    setBusy(true);
    try {
      await register(reg);
      toast.success(reg.role === "client" ? "Account created! We emailed you a 6-digit code to verify your phone." : "Account created!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  const field = "mt-1 bg-white border-slate-300";

  return (
    <div className="max-w-md mx-auto px-4 py-14">
      <div className="text-center mb-6">
        <img src={LOGO_URL} alt="Care-4U" className="h-16 mx-auto object-contain" />
      </div>
      <div className="card-metal p-6">
        <Tabs defaultValue={validPreset || refCode ? "register" : "login"}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="login" data-testid="tab-login">Login</TabsTrigger>
            <TabsTrigger value="register" data-testid="tab-register">Register</TabsTrigger>
          </TabsList>
          {refCode && (
            <p className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg p-2 mt-3 text-center" data-testid="referral-invite-note">
              🎁 You were invited by a friend! Sign up as a client and their invite code <b>{refCode}</b> is applied automatically.
            </p>
          )}

          <TabsContent value="login">
            {forgot ? (
              forgotSent ? (
                <div className="space-y-4 mt-4 text-center" data-testid="forgot-sent">
                  <p className="text-slate-700">
                    If an account exists for <b>{forgotEmail}</b>, we've emailed a password reset link.
                    Check your inbox (and spam) — the link expires in 1 hour.
                  </p>
                  <button type="button" onClick={() => { setForgot(false); setForgotSent(false); }}
                    className="btn-silver-3d" data-testid="back-to-login-btn">
                    <span className="text-engraved">Back to Sign In</span>
                  </button>
                </div>
              ) : (
                <form onSubmit={doForgot} className="space-y-4 mt-4" data-testid="forgot-form">
                  <p className="text-sm text-slate-600">
                    Enter your account email and we'll send you a secure link to reset your password.
                  </p>
                  <div>
                    <Label>Email</Label>
                    <Input type="email" required className={field} data-testid="forgot-email"
                      value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} />
                  </div>
                  <button type="submit" disabled={busy} data-testid="forgot-submit" className="btn-red-3d w-full">
                    {busy ? "Sending…" : "Send Reset Link"}
                  </button>
                  <button type="button" onClick={() => setForgot(false)}
                    className="text-sm text-slate-500 hover:text-[#9b111e] w-full text-center" data-testid="cancel-forgot-btn">
                    Back to Sign In
                  </button>
                </form>
              )
            ) : (
              <form onSubmit={doLogin} className="space-y-4 mt-4">
                <div>
                  <Label>Email</Label>
                  <Input type="email" required className={field} data-testid="login-email"
                    value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input type="password" required className={field} data-testid="login-password"
                    value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} />
                </div>
                <button type="submit" disabled={busy} data-testid="login-submit" className="btn-red-3d w-full">
                  {busy ? "Signing in…" : "Sign In"}
                </button>
                <button type="button" onClick={() => setForgot(true)}
                  className="text-sm text-slate-500 hover:text-[#9b111e] w-full text-center" data-testid="forgot-password-link">
                  Forgot my password?
                </button>
              </form>
            )}
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={doRegister} className="space-y-4 mt-4">
              {reg.role === "professional" && (
                <div className="rounded-xl border-2 border-[#9b111e] bg-red-50 p-4" data-testid="pro-apply-notice">
                  <p className="text-sm text-slate-800 font-semibold">Applying to become a Care-4U Pro</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                    This first step just creates your login. Right after, you'll complete your <b>application</b> —
                    your 4+ years of work / school / military experience (with locations & job titles),
                    <b> supporting documents</b>, certifications and services. It's then emailed to the administrator for approval.
                  </p>
                  <p className="text-xs text-slate-700 mt-2 leading-relaxed bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="pro-featured-perk">
                    🏆 <b>Perk:</b> our highest-rated pro each week becomes the <b>Featured Pro of the Week</b> —
                    spotlighted on our homepage <b>and boosted to the top of search results</b>. Do great work, get seen first!
                  </p>
                  <p className="text-xs text-slate-700 mt-2 leading-relaxed bg-green-50 border border-green-200 rounded-lg p-2" data-testid="pro-comeback-notice">
                    ⚡ <b>The comeback rule:</b> if you're ever moved to discounted Back-Up status under our NO BAD APPLES
                    system, <b>3 perfect 10/10 reviews INSTANTLY restore you to full status</b> and full price.
                  </p>
                  <p className="text-xs text-slate-700 mt-2 leading-relaxed bg-slate-100 border border-slate-300 rounded-lg p-2" data-testid="pro-qc-notice">
                    🔍 <b>Heads up — random quality-control checks:</b> after visits, clients may be randomly asked to confirm you
                    <b> knocked within 5 minutes</b> of the booking time, <b>dressed to code with foot covers</b> (collared shirt,
                    jeans or khakis, no athletic gear, no hats), <b>wore your CARE-4U lanyard</b>, and <b>greeted them by their
                    nickname</b>. <b className="text-[#9b111e]">Failing any item drops you immediately to Back-Up Pro status (20% off rates)</b> —
                    but pass your next two checks cleanly and the strike is cleared.
                  </p>
                </div>
              )}
              {reg.role === "client" && (
                <div className="rounded-xl border-2 border-green-500 bg-green-50 p-4" data-testid="client-no-bad-apples-notice">
                  <p className="text-sm text-slate-800 font-bold">🍎🚫 We have NO BAD APPLES!</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                    Every pro is held to our automatic quality standard — low ratings or no-shows drop them to
                    discounted Back-Up status until they earn their way back. Book with total confidence.
                  </p>
                </div>
              )}
              {reg.role === "client" && (
                <div className="rounded-xl border-2 border-blue-400 bg-blue-50 p-4" data-testid="signup-referral-perks">
                  <p className="text-sm text-slate-800 font-bold flex items-center gap-2 flex-wrap">
                    🎁 Refer friends, get rewarded
                    <span className="inline-flex items-center gap-1 text-[10px] font-black text-white bg-blue-600 rounded-full px-2.5 py-1" data-testid="signup-vip-badge">
                      💎 REFERRAL VIP · 10% OFF
                    </span>
                  </p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                    Earn <b>$10 site credit</b> every time a friend you invite completes their first booking.
                    Hit <b>3 successful referrals</b> and you unlock <b>💎 Referral VIP status</b> —
                    <b> 10% off ALL your future bookings, automatically, for life</b>.
                  </p>
                </div>
              )}
              <div>
                <Label>I am a…</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {ROLES.map((r) => (
                    <button
                      type="button"
                      key={r.id}
                      data-testid={`role-${r.id}`}
                      onClick={() => setReg({ ...reg, role: r.id })}
                      className={`p-2 rounded-lg border text-xs font-bold transition-colors ${
                        reg.role === r.id
                          ? "border-[#9b111e] bg-red-50 text-[#9b111e]"
                          : "border-slate-300 text-slate-600"
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {ROLES.find((r) => r.id === reg.role)?.desc}. (Administrator is a fixed account.)
                </p>
              </div>
              <div>
                <Label>Full Name</Label>
                <Input required className={field} data-testid="reg-name"
                  value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" required className={field} data-testid="reg-email"
                  value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} />
              </div>
              <div>
                <Label>Phone Number{reg.role === "client" && <span className="text-[#9b111e]"> *</span>}</Label>
                <Input type="tel" required={reg.role === "client"} className={field} data-testid="reg-phone"
                  placeholder="(555) 123-4567"
                  value={reg.phone} onChange={(e) => setReg({ ...reg, phone: e.target.value })} />
                {reg.role === "client" && (
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed" data-testid="reg-phone-note">
                    🔒 Your phone number is used <b>only</b> to collect detailed feedback about the professionals'
                    services you receive. It's kept secure by our administrator and is{" "}
                    <b className="text-[#9b111e]">NEVER shared with any third party</b>.
                  </p>
                )}
              </div>
              <div>
                <Label>Password</Label>
                <Input type="password" required className={field} data-testid="reg-password"
                  value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label>City</Label><Input className={field} data-testid="reg-city" value={reg.city} onChange={(e) => setReg({ ...reg, city: e.target.value })} /></div>
                <div><Label>State</Label><Input className={field} data-testid="reg-state" value={reg.state} onChange={(e) => setReg({ ...reg, state: e.target.value })} /></div>
                <div><Label>Zip</Label><Input className={field} data-testid="reg-zip" value={reg.zip} onChange={(e) => setReg({ ...reg, zip: e.target.value })} /></div>
              </div>
              <button type="button" onClick={geolocate} data-testid="reg-geo" className="btn-silver-3d w-full !py-2">
                <MapPin className="w-4 h-4 text-[#9b111e]" />
                <span className="text-engraved text-sm">{reg.lat ? "Location Set ✓" : "Use My Location"}</span>
              </button>
              {reg.role === "client" && (
                <label className="flex items-start gap-3 rounded-xl border border-[#9b111e]/30 bg-red-50 p-4 cursor-pointer" data-testid="home-pledge-wrap">
                  <input type="checkbox" required className="mt-1 w-4 h-4 accent-[#9b111e]" data-testid="reg-home-pledge"
                    checked={reg.home_pledge} onChange={(e) => setReg({ ...reg, home_pledge: e.target.checked })} />
                  <span className="text-sm text-slate-700">
                    <b className="text-[#9b111e]">My Pledge:</b> I promise to <b>always be home</b> for every booking I schedule,
                    and to keep my residence a <b>neat, tidy and SAFE working space</b> — free of clutter and garbage — while
                    <b> warmly welcoming my pro at the door by their nickname</b>, so our mobile professionals feel respected and never make a wasted trip.
                  </span>
                </label>
              )}
              <button type="submit" disabled={busy} data-testid="register-submit" className="btn-red-3d w-full">
                {busy ? "Creating…" : reg.role === "professional" ? "Create Account & Start Application" : "Create Account"}
              </button>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
